import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../formStyles.css';
import './carrierForm.css';
import {
  fetchCarrierByCode,
  createCarrier,
  updateCarrier,
  deleteCarrier,
  duplicateCarrier,
} from '../../services/api';
import MessageBox from '../MessageBox/MessageBox';
import MailBook from '../MailBook/MailBook';
import PostcodeCityInputs from '../PostcodeCityInputs/PostcodeCityInputs';
import IconButton from '../IconButton/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import StarIcon from '@mui/icons-material/Star';
import { useForm } from '../BaseForm/FormContext';
import { defaultCarrierData } from '../../dataSets/defaultFormData';
import { useEstablishmentSync } from '../../hooks/useEstablishmentSync';
import { formatDateStringToFrenchDate, formatDateTimeToFrenchDate } from '../../utils/dateUtils';

export default function CarrierForm() {
  const {
    isLoading,
    setIsLoading,
    isNew,
    setIsNew,
    formData,
    setFormData,
    messageBoxConfig,
    setMessageBoxConfig,
    showMessageBox,
    setShowMessageBox,
    handleChange,
    handleBack,
    handleConfirmBack,
    handleFakeFormClick,
    setHasChanged,
  } = useForm();
  const { carrier_code } = useParams();
  const { handleChangeWithSync } = useEstablishmentSync(
    formData,
    setFormData,
    handleChange,
    'carrier',
    setHasChanged,
    isLoading
  );

  const [showMailBook, setShowMailBook] = useState(false);
  const [pickupDeptsList, setPickupDeptsList] = useState([]);
  const [deliveryDeptsList, setDeliveryDeptsList] = useState([]);
  const [pickupDeptsNumber, setPickupDeptsNumber] = useState(1);
  const [deliveryDeptsNumber, setDeliveryDeptsNumber] = useState(1);

  useEffect(() => {
    const fetchData = async carrier_code => {
      if (carrier_code && carrier_code !== 'nouveau') {
        try {
          const fetchedData = await fetchCarrierByCode(carrier_code);
          const updatedFormData = {};
          Object.keys(formData).forEach(key => {
            updatedFormData[key] =
              fetchedData[key] !== undefined && fetchedData[key] !== null
                ? fetchedData[key]
                : formData[key];
          });

          setFormData(updatedFormData);
          setIsLoading(false);
        } catch (error) {
          console.error('Erreur lors de la récupération du transporteur:', error);
        }
      } else if (carrier_code && carrier_code === 'nouveau') {
        setFormData(defaultCarrierData);
        setIsLoading(false);
      }
    };

    setIsNew(carrier_code === 'nouveau');
    fetchData(carrier_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrier_code]);

  useEffect(() => {
    setPickupDeptsList(formData.pickup_departments.split(','));
    setDeliveryDeptsList(formData.delivery_departments.split(','));
    setPickupDeptsNumber(formData.pickup_departments.split(',').length);
    setDeliveryDeptsNumber(formData.delivery_departments.split(',').length);
  }, [formData.pickup_departments, formData.delivery_departments]);

  const handlePickupDeptsChange = e => {
    const newList = [...pickupDeptsList];
    newList[e.target.id] = e.target.value;
    setPickupDeptsList(newList);
    setFormData({
      ...formData,
      pickup_departments: newList.join(','),
    });
  };

  const handleDeliveryDeptsChange = e => {
    const newList = [...deliveryDeptsList];
    newList[e.target.id] = e.target.value;
    setDeliveryDeptsList(newList);
    setFormData({
      ...formData,
      delivery_departments: newList.join(','),
    });
  };

  const handleVehicleQtyChange = e => {
    const newVehicleData = [
      e.target.value,
      formData[e.target.name] ? formData[e.target.name].split(',')[1] : '',
    ];
    setFormData({
      ...formData,
      [e.target.name]: newVehicleData.join(','),
    });
  };

  const handleVehicleLifterChange = e => {
    const newVehicleData = [
      formData[e.target.name] ? formData[e.target.name].split(',')[0] : '',
      formData[e.target.name]
        ? formData[e.target.name].split(',')[0] !== ''
          ? e.target.value
          : ''
        : '',
    ];
    setFormData({
      ...formData,
      [e.target.name]: newVehicleData.join(','),
    });
  };

  const handleBlurName = () => {
    let updatedData = {
      ...formData,
    };
    if (!formData.billing_name) {
      updatedData = {
        ...updatedData,
        billing_name: formData.name,
      };
    }
    setFormData(updatedData);
  };

  const handleShowMailbook = () => {
    setShowMailBook(true);
  };

  const handleCreateCarrier = e => {
    if (formData.carrier_code && formData.name) {
      handleSubmit(e, formData);
    } else {
      setMessageBoxConfig({
        message: `Merci de renseigner un code tps et un nom pour ce transporteur avant de le créer.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleModifyCarrier = e => {
    if (formData.carrier_code && formData.name) {
      handleSubmit(e, formData);
    } else {
      setMessageBoxConfig({
        message: `Le code tps et le nom doivent être renseignés.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleDeleteCarrier = async () => {
    const DeleteCarrier = async () => {
      try {
        const response = await deleteCarrier(formData);
        if (response.status === 204) {
          setMessageBoxConfig({
            message: `Le transporteur ${carrier_code} a été supprimé définitivement.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setShowMessageBox(false);
              handleBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        } else {
          setMessageBoxConfig({
            message: `La suppression du transporteur ${carrier_code} a rencontré un comportement innatendu: code ${response.status}.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      } catch (error) {
        setMessageBoxConfig({
          message: `La suppression du transporteur ${carrier_code} a rencontré une erreur. La suppression est impossible si un ou plusieurs dossiers existent pour ce transporteur.`,
          type: 'ok',
          loading_button: false,
          input: false,
          onConfirm: () => setShowMessageBox(false),
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      }
    };

    setMessageBoxConfig({
      message: `Etes vous certain.e de vouloir supprimer le transporteur ${carrier_code} ?`,
      type: 'yesno',
      loading_button: false,
      input: false,
      onConfirm: () => {
        setShowMessageBox(false);
        DeleteCarrier();
      },
      onCancel: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const handleDuplicateCarrier = async () => {
    setMessageBoxConfig({
      message: 'Entrez le nouveau code transporteur:',
      type: 'input',
      loading_button: false,
      input: true,
      onConfirm: async newCarrierCode => {
        if (!newCarrierCode || newCarrierCode.trim() === '') {
          setMessageBoxConfig({
            message: 'Le code transporteur ne peut pas être vide.',
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
          return;
        }

        setShowMessageBox(false);

        // Attendre un court délai pour que la MessageBox se démonte et remonte
        await new Promise(resolve => setTimeout(resolve, 100));

        setMessageBoxConfig({
          message: 'Entrez le nouveau nom du transporteur:',
          type: 'input',
          loading_button: false,
          input: true,
          onConfirm: async newName => {
            if (!newName || newName.trim() === '') {
              setMessageBoxConfig({
                message: 'Le nom du transporteur ne peut pas être vide.',
                type: 'ok',
                loading_button: false,
                input: false,
                onConfirm: () => setShowMessageBox(false),
                onCancel: () => setShowMessageBox(false),
              });
              setShowMessageBox(true);
              return;
            }

            setMessageBoxConfig({
              message: `Duplication du transporteur ${carrier_code} en cours...`,
              type: 'loading',
              loading_button: true,
              input: false,
            });
            setShowMessageBox(true);

            try {
              const response = await duplicateCarrier(carrier_code, newCarrierCode, newName);
              if (response.status === 201) {
                setMessageBoxConfig({
                  message: `Le transporteur ${carrier_code} a été dupliqué avec succès. Nouveau code: ${newCarrierCode}`,
                  type: 'ok',
                  loading_button: false,
                  input: false,
                  onConfirm: () => {
                    setShowMessageBox(false);
                    window.location.href = `/transporteurs/${newCarrierCode}`;
                  },
                  onCancel: () => setShowMessageBox(false),
                });
                setShowMessageBox(true);
              } else {
                setMessageBoxConfig({
                  message: `La duplication du transporteur ${carrier_code} a rencontré un comportement inattendu: code ${response.status}.`,
                  type: 'ok',
                  loading_button: false,
                  input: false,
                  onConfirm: () => setShowMessageBox(false),
                  onCancel: () => setShowMessageBox(false),
                });
                setShowMessageBox(true);
              }
            } catch (error) {
              const errorMessage =
                error.response?.data?.error ||
                `La duplication du transporteur ${carrier_code} a rencontré une erreur.`;
              setMessageBoxConfig({
                message: errorMessage,
                type: 'ok',
                loading_button: false,
                input: false,
                onConfirm: () => setShowMessageBox(false),
                onCancel: () => setShowMessageBox(false),
              });
              setShowMessageBox(true);
            }
          },
          onCancel: () => setShowMessageBox(false),
        });
        setShowMessageBox(true);
      },
      onCancel: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const handleSubmit = async (e, updatedData) => {
    e.preventDefault();

    try {
      if (isNew) {
        const response = await createCarrier(updatedData);
        if (response.status === 201) {
          setMessageBoxConfig({
            message: `Le transporteur ${formData.carrier_code} a bien été créé.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setHasChanged(false);
              setShowMessageBox(false);
              handleConfirmBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      } else {
        const response = await updateCarrier(updatedData);
        if (response.status === 200) {
          setMessageBoxConfig({
            message: `Le transporteur ${formData.carrier_code} a bien été mise à jour.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => {
              setHasChanged(false);
              setShowMessageBox(false);
              handleConfirmBack();
            },
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      }
    } catch (error) {
      setMessageBoxConfig({
        message: `Erreur lors de la sauvegarde du transporteur: ${error}.`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => setShowMessageBox(false),
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleAddPickupDept = () => {
    setPickupDeptsNumber(pickupDeptsNumber + 1);
  };

  const handleAddDeliveryDept = () => {
    setDeliveryDeptsNumber(deliveryDeptsNumber + 1);
  };

  const handleCloseMailBook = () => {
    setShowMailBook(false);
  };

  const handleConfirmMailBook = async ({ email_book }) => {
    if (email_book !== formData.email_book) {
      const updatedFormData = {
        ...formData,
        email_book,
      };

      if (isNew) {
        // Pour un nouveau transporteur, on met à jour seulement le formData local
        // Les emails seront sauvegardés lors de la création du transporteur
        setFormData(updatedFormData);
        setHasChanged(true);
        setShowMailBook(false);
      } else {
        // Pour un transporteur existant, on sauvegarde immédiatement
        try {
          const response = await updateCarrier(updatedFormData);
          if (response.status === 200) {
            setFormData(updatedFormData);
            setShowMailBook(false);
          }
        } catch (error) {
          setMessageBoxConfig({
            message: `Erreur lors de la sauvegarde du carnet de mails: ${error}.`,
            type: 'ok',
            loading_button: false,
            input: false,
            onConfirm: () => setShowMessageBox(false),
            onCancel: () => setShowMessageBox(false),
          });
          setShowMessageBox(true);
        }
      }
    } else {
      setShowMailBook(false);
    }
  };

  const handleCancelMailBook = () => {
    setShowMailBook(false);
  };

  const handleToggleBlacklist = () => {
    setFormData({
      ...formData,
      is_blacklisted: !formData.is_blacklisted,
      is_favorite: false,
    });
    setHasChanged(true);
  };

  return (
    <div>
      <div className="form-style">
        <div className="form-header grid-layout">
          <div className="goBackButton">
            <button type="button" onClick={handleBack}>
              Retour
            </button>
          </div>
          <div className="form-title-container">
            {isNew ? <h1>Nouveau transporteur</h1> : <h1>Transporteur {formData.carrier_code}</h1>}
          </div>
          <div className="form-header-right-container">
            {isNew && process.env.NODE_ENV !== 'production' && (
              <button className="fake-form-fill" type="button" onClick={handleFakeFormClick}>
                Générer un faux transporteur
              </button>
            )}
            {!isNew && (
              <div className="form-header-button-container">
                {!formData.is_blacklisted && (
                  <IconButton
                    icon={BlockIcon}
                    text=""
                    onClick={handleToggleBlacklist}
                    variant="contained"
                    styleClass={'no-background blacklist-button'}
                    title="Blacklister ce transporteur"
                  />
                )}
                <IconButton
                  icon={ContentCopyIcon}
                  text=""
                  onClick={handleDuplicateCarrier}
                  variant="contained"
                  styleClass={'no-background'}
                  title="Dupliquer le transporteur"
                />
              </div>
            )}
          </div>
        </div>

        {!isNew && formData.is_blacklisted && (
          <div className="carrier-status-banner-container">
            <div className="carrier-status-banner blacklisted">
              <BlockIcon />
              <span>Transporteur blacklisté</span>
              <button
                type="button"
                className="banner-action-button"
                onClick={handleToggleBlacklist}
              >
                Retirer de la blacklist
              </button>
            </div>
          </div>
        )}

        {!isNew && formData.is_favorite && (
          <div className="carrier-status-banner-container">
            <div className="carrier-status-banner favorite">
              <StarIcon />
              <span>Transporteur favori</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-style-client-name form-div">
            <div className="special-field-container">
              <div className="special-field full form-div">
                <h3>Code transporteur:</h3>
                <input
                  type="text"
                  name="carrier_code"
                  value={formData.carrier_code}
                  onChange={handleChange}
                  disabled={!isNew}
                />
              </div>
              {!isNew && (
                <div className="special-field dates form-div">
                  {formData.creation_date && (
                    <p>Créé le {formatDateStringToFrenchDate(formData.creation_date)}</p>
                  )}
                  {formData.updated_at && (
                    <p>Modifié le {formatDateTimeToFrenchDate(formData.updated_at)}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-style-establishment-title form-div">
            <h2>Adresse établissement (siège)</h2>
          </div>

          <div className="form-style-establishment-1 form-div">
            <div className="horizontal-input-group">
              <label>Nom du transporteur :</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChangeWithSync}
                onBlur={handleBlurName}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name="establishment_address1"
                value={formData.establishment_address1}
                onChange={handleChangeWithSync}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name="establishment_address2"
                value={formData.establishment_address2}
                onChange={handleChangeWithSync}
              />
            </div>
            <PostcodeCityInputs
              prefix="establishment"
              postCodeFieldName="establishment_postcode"
              cityFieldName="establishment_city"
              handleChange={handleChangeWithSync}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
            />
            <div className="horizontal-input-group">
              <label>Pays :</label>
              <input
                type="text"
                name="establishment_country"
                value={formData.establishment_country}
                onChange={handleChangeWithSync}
              />
            </div>
          </div>

          <div className="form-style-establishment-2 form-div">
            <div className="horizontal-input-group">
              <label>Contact :</label>
              <input
                type="text"
                name="establishment_contact"
                value={formData.establishment_contact}
                onChange={handleChangeWithSync}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 1 :</label>
              <input
                type="text"
                name="establishment_phone1"
                value={formData.establishment_phone1}
                onChange={handleChangeWithSync}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 2 :</label>
              <input
                type="text"
                name="establishment_phone2"
                value={formData.establishment_phone2}
                onChange={handleChangeWithSync}
              />
            </div>
            {/* <div className="horizontal-input-group">
              <label>Télécopie :</label>
              <input
                type="text"
                name="establishment_fax"
                value={formData.establishment_fax}
                onChange={handleChange}
              />
            </div> */}
            <div className="horizontal-input-group withrightbutton">
              <label>Mail :</label>
              <input
                type="text"
                name="establishment_email"
                value={formData.establishment_email}
                onChange={handleChangeWithSync}
              />
              <button type="button" className="rightbutton" onClick={handleShowMailbook}>
                Carnet
              </button>
            </div>
            <div className="horizontal-input-group">
              <label>Mail CMR :</label>
              <input
                type="text"
                name="cmr_email"
                value={formData.cmr_email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-address-title form-div">
            <h2>Adresse facturation</h2>
          </div>

          <div className="form-style-billing-address-1 form-div">
            <div className="horizontal-input-group">
              <label>Nom du transporteur :</label>
              <input
                type="text"
                name="billing_name"
                value={formData.billing_name}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name="billing_address1"
                value={formData.billing_address1}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name="billing_address2"
                value={formData.billing_address2}
                onChange={handleChange}
              />
            </div>
            <PostcodeCityInputs
              prefix="billing"
              postCodeFieldName="billing_postcode"
              cityFieldName="billing_city"
              handleChange={handleChange}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
            />
            <div className="horizontal-input-group">
              <label>Pays :</label>
              <input
                type="text"
                name="billing_country"
                value={formData.billing_country}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-address-2 form-div">
            <div className="horizontal-input-group">
              <label>Contact :</label>
              <input
                type="text"
                name="billing_contact"
                value={formData.billing_contact}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 1 :</label>
              <input
                type="text"
                name="billing_phone1"
                value={formData.billing_phone1}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Téléphone 2 :</label>
              <input
                type="text"
                name="billing_phone2"
                value={formData.billing_phone2}
                onChange={handleChange}
              />
            </div>
            {/* <div className="horizontal-input-group">
              <label>Télécopie :</label>
              <input
                type="text"
                name="billing_fax"
                value={formData.billing_fax}
                onChange={handleChange}
              />
            </div> */}
            <div className="horizontal-input-group">
              <label>Mail :</label>
              <input
                type="text"
                name="billing_email"
                value={formData.billing_email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-title form-div">
            <h2>Facturation</h2>
          </div>

          <div className="form-style-billing-1 form-div">
            <div className="horizontal-input-group">
              <label>N° de SIRET :</label>
              <input
                type="text"
                name="siret_number"
                value={formData.siret_number}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>N° de TVA :</label>
              <input
                type="text"
                name="vat_number"
                value={formData.vat_number}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Code APE / NAF :</label>
              <input
                type="text"
                name="ape_naf_code"
                value={formData.ape_naf_code}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-billing-2 form-div">
            <div className="multiple-radio-inputs-grid client">
              <label className="radio-group-main-label">Type de paiement :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="payment_type"
                    id="payment_type_virement"
                    value="VIREMENT"
                    checked={formData.payment_type === 'VIREMENT'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="payment_type_virement">
                    Virement
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="payment_type"
                    id="payment_type_cheque"
                    value="CHEQUE"
                    checked={formData.payment_type === 'CHEQUE'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="payment_type_cheque">
                    Chèque
                  </label>
                </div>
              </div>
              <label className="radio-group-main-label">Période :</label>
              <div className="yes-no-radio-group">
                <div>
                  <input
                    type="radio"
                    name="period"
                    id="period_quinzaine"
                    value="QUINZAINE"
                    checked={formData.period === 'QUINZAINE'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="period_quinzaine">
                    Quinzaine
                  </label>
                </div>
                <div>
                  <input
                    type="radio"
                    name="period"
                    id="period_mois"
                    value="MOIS"
                    checked={formData.period === 'MOIS'}
                    onChange={handleChange}
                  />
                  <label className="yes-no-radio-group-label" htmlFor="period_mois">
                    Mois
                  </label>
                </div>
              </div>
              <div className="checkbox-input-line">
                <label>Afficher All-In sur ordre</label>
                <input
                  type="checkbox"
                  name="display_all_in"
                  checked={formData.display_all_in}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-style-exploitation-title form-div">
            <h2>Exploitation</h2>
          </div>

          <div className="form-style-exploitation-1 form-div">
            <div className="horizontal-input-group sub-group">
              <label className="horizontal-input-group title">Départements d'enlèvement</label>
              <div className="multiple-text-inputs-with-addbutton form-div">
                {Array.from({ length: pickupDeptsNumber }).map((_, index) => (
                  <input
                    type="number"
                    name="pickup_department"
                    id={index}
                    key={`pickup_departments_${index}`}
                    value={
                      index < formData.pickup_departments.split(',').length
                        ? formData.pickup_departments.split(',')[index]
                        : ''
                    }
                    onChange={handlePickupDeptsChange}
                  />
                ))}
                <div className="addbutton-container form-div">
                  <button type="button" onClick={handleAddPickupDept}>
                    +
                  </button>
                </div>
              </div>
            </div>
            <div className="horizontal-input-group sub-group">
              <label className="horizontal-input-group title">Départements de livraison</label>
              <div className="multiple-text-inputs-with-addbutton form-div">
                {Array.from({ length: deliveryDeptsNumber }).map((_, index) => (
                  <input
                    type="number"
                    name="delivery_departments"
                    id={index}
                    key={`delivery_departments_${index}`}
                    value={
                      index < formData.delivery_departments.split(',').length
                        ? formData.delivery_departments.split(',')[index]
                        : ''
                    }
                    onChange={handleDeliveryDeptsChange}
                  />
                ))}
                <div className="addbutton-container form-div">
                  <button type="button" onClick={handleAddDeliveryDept}>
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="form-style-exploitation-2 form-div">
            <div className="horizontal-input-group sub-group">
              <label className="horizontal-input-group title">Véhicules</label>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Semi taut :</label>
                <input
                  type="text"
                  name="semi_taut"
                  value={formData.semi_taut.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="semi_taut"
                      id="semi_taut_lifter_true"
                      value="true"
                      checked={formData.semi_taut.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="semi_taut_lifter_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="semi_taut"
                      id="semi_taut_lifter_false"
                      value="false"
                      checked={formData.semi_taut.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="semi_taut_lifter_false">
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Porteur taut :</label>
                <input
                  type="text"
                  name="porteur_taut"
                  value={formData.porteur_taut.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="porteur_taut"
                      id="porteur_taut_lifter_true"
                      value="true"
                      checked={formData.porteur_taut.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="porteur_taut_lifter_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="porteur_taut"
                      id="porteur_taut_lifter_false"
                      value="false"
                      checked={formData.porteur_taut.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="porteur_taut_lifter_false">
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Semi fourgon :</label>
                <input
                  type="text"
                  name="semi_fourgon"
                  value={formData.semi_fourgon.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="semi_fourgon"
                      id="semi_fourgon_lifter_true"
                      value="true"
                      checked={formData.semi_fourgon.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="semi_fourgon_lifter_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="semi_fourgon"
                      id="semi_fourgon_lifter_false"
                      value="false"
                      checked={formData.semi_fourgon.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="semi_fourgon_lifter_false">
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Porteur fourgon :</label>
                <input
                  type="text"
                  name="porteur_fourgon"
                  value={formData.porteur_fourgon.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="porteur_fourgon"
                      id="porteur_fourgon_lifter_true"
                      value="true"
                      checked={formData.porteur_fourgon.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="porteur_fourgon_lifter_true"
                    >
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="porteur_fourgon"
                      id="porteur_fourgon_lifter_false"
                      value="false"
                      checked={formData.porteur_fourgon.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="porteur_fourgon_lifter_false"
                    >
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Semi frigo :</label>
                <input
                  type="text"
                  name="semi_frigo"
                  value={formData.semi_frigo.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="semi_frigo"
                      id="semi_frigo_lifter_true"
                      value="true"
                      checked={formData.semi_frigo.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="semi_frigo_lifter_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="semi_frigo"
                      id="semi_frigo_lifter_false"
                      value="false"
                      checked={formData.semi_frigo.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="semi_frigo_lifter_false">
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Porteur frigo :</label>
                <input
                  type="text"
                  name="porteur_frigo"
                  value={formData.porteur_frigo.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="porteur_frigo"
                      id="porteur_frigo_lifter_true"
                      value="true"
                      checked={formData.porteur_frigo.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="porteur_frigo_lifter_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="porteur_frigo"
                      id="porteur_frigo_lifter_false"
                      value="false"
                      checked={formData.porteur_frigo.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="porteur_frigo_lifter_false"
                    >
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">20M3 :</label>
                <input
                  type="text"
                  name="vingt_metres_cube"
                  value={formData.vingt_metres_cube.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="vingt_metres_cube"
                      id="vingt_metres_cube_lifter_true"
                      value="true"
                      checked={formData.vingt_metres_cube.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="vingt_metres_cube_lifter_true"
                    >
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="vingt_metres_cube"
                      id="vingt_metres_cube_lifter_false"
                      value="false"
                      checked={formData.vingt_metres_cube.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="vingt_metres_cube_lifter_false"
                    >
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">20M3 frigo :</label>
                <input
                  type="text"
                  name="vingt_metres_cube_frigo"
                  value={formData.vingt_metres_cube_frigo.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="vingt_metres_cube_frigo"
                      id="vingt_metres_cube_frigo_lifter_true"
                      value="true"
                      checked={formData.vingt_metres_cube_frigo.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="vingt_metres_cube_frigo_lifter_true"
                    >
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="vingt_metres_cube_frigo"
                      id="vingt_metres_cube_frigo_lifter_false"
                      value="false"
                      checked={formData.vingt_metres_cube_frigo.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label
                      className="yes-no-radio-group-label"
                      htmlFor="vingt_metres_cube_frigo_lifter_false"
                    >
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="horizontal-input-group withrightradio">
                <label className="vehicule-label">Plateau :</label>
                <input
                  type="text"
                  name="plateau"
                  value={formData.plateau.split(',')[0]}
                  onChange={handleVehicleQtyChange}
                />
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="plateau"
                      id="plateau_lifter_true"
                      value="true"
                      checked={formData.plateau.split(',')[1] === 'true'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="plateau_lifter_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="plateau"
                      id="plateau_lifter_false"
                      value="false"
                      checked={formData.plateau.split(',')[1] === 'false'}
                      onChange={handleVehicleLifterChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="plateau_lifter_false">
                      Non
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-style-endbuttons">
            <div className="endbuttons-container">
              <button
                type="button"
                onClick={handleCreateCarrier}
                className={!isNew ? 'hidden-preserve-space' : ''}
              >
                Créer
              </button>
              <button
                type="button"
                onClick={handleModifyCarrier}
                className={isNew ? 'hidden-preserve-space' : ''}
              >
                Modifier
              </button>
              <button
                type="button"
                className={`delete-button ${isNew ? 'hidden-preserve-space' : ''}`}
                onClick={handleDeleteCarrier}
              >
                Supprimer
              </button>
            </div>
          </div>
        </form>

        {showMailBook && (
          <MailBook
            email_book={formData.email_book}
            onClose={handleCloseMailBook}
            onConfirm={handleConfirmMailBook}
            onCancel={handleCancelMailBook}
          />
        )}

        {showMessageBox && (
          <MessageBox
            message={messageBoxConfig.message}
            onClose={() => setShowMessageBox(false)}
            onConfirm={messageBoxConfig.onConfirm}
            onCancel={messageBoxConfig.onCancel}
            type={messageBoxConfig.type}
            loading_button={messageBoxConfig.loading_button}
            input={messageBoxConfig.input}
            technicalDetails={messageBoxConfig.technicalDetails}
          />
        )}
      </div>
    </div>
  );
}
